import { components } from "@/api-schema";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { EmptyValue } from "@/components/ui/empty-value";
import { type ExpandableBadgeItem, ExpandableBadgeList } from "@/components/ui/expandable-badge-list";
import i18next from "i18next";
import { commonColumns } from "./_constants/common-columns";

export type RuleTableType = components["schemas"]["Rule"];

type NamedEntity = { uuid?: string | null; name?: string | null };

function toNamedItems(entities: NamedEntity[] | null | undefined): ExpandableBadgeItem[] {
  return (entities ?? []).map((entity) => ({
    key: entity.uuid ?? entity.name ?? "",
    label: entity.name ?? "",
  }));
}

function sortByName<T extends NamedEntity>(entities: T[] | null | undefined): T[] {
  return [...(entities ?? [])].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
}

export const ruleTableDefaultColumns = {
  columnCompany: {
    id: "companies",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("common:Companies"),
    accessorFn: (row) => row.companies,
    cell: (row) => {
      const companies = row.getValue() as components["schemas"]["Company"][] | undefined;
      return <ExpandableBadgeList items={toNamedItems(sortByName(companies))} />;
    },
  },
  columnCompanyGroups: {
    id: "companyGroups",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("common:CompanyGroups"),
    accessorFn: (row) => row.companyGroups,
    cell: (row) => {
      const companyGroups = row.getValue() as components["schemas"]["CompanyGroup"][] | undefined;
      return <ExpandableBadgeList items={toNamedItems(sortByName(companyGroups))} />;
    },
  },
  columnBranches: {
    id: "branches",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("common:Branches"),
    accessorFn: (row) => row.branches,
    cell: (row) => {
      const branches = row.getValue() as components["schemas"]["Branch"][] | undefined;
      const sorted = [...(branches ?? [])].sort((a, b) => a.name.localeCompare(b.name));
      const items = sorted.map((branch) => ({
        key: branch.uuid ?? branch.name,
        label: branch.name,
      }));
      return <ExpandableBadgeList items={items} />;
    },
  },
  columnDepartment: {
    id: "departments",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("ram:Departments"),
    accessorFn: (row) => row.departments,
    cell: (row) => {
      const departments = row.getValue() as components["schemas"]["Department"][] | undefined;
      return <ExpandableBadgeList items={toNamedItems(sortByName(departments))} />;
    },
  },
  columnOccupation: {
    id: "occupations",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("ram:Occupations"),
    accessorFn: (row) => row.occupations,
    cell: (row) => {
      const occupations = row.getValue() as components["schemas"]["Occupation"][] | undefined;
      return <ExpandableBadgeList items={toNamedItems(sortByName(occupations))} />;
    },
  },
  columnBrand: {
    id: "brands",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("ram:Brands"),
    accessorFn: (row) => row.brands,
    cell: (row) => {
      const brands = row.getValue() as components["schemas"]["Brand"][] | undefined;
      return <ExpandableBadgeList items={toNamedItems(sortByName(brands))} />;
    },
  },
  columnJobFunctions: {
    id: "jobFunctions",
    size: 300,
    enableSorting: true,
    header: () => i18next.t("ram:JobFunctions"),
    accessorFn: (row) => row.jobFunctions,
    cell: (row) => {
      const jobFunctions = row.getValue() as components["schemas"]["JobFunction"][] | undefined;
      return <ExpandableBadgeList items={toNamedItems(sortByName(jobFunctions))} />;
    },
  },
  columnDeletedAt: {
    id: "deletedAt",
    size: 150,
    enableSorting: true,
    header: () => i18next.t("common:Deleted"),
    align: "right" as const,
    accessorFn: (row) => row.deletedAt,
    cell: (row) => {
      const date = row.getValue() as string;
      return <div className={date ? "text-error" : ""}>{date ? formatDateToLocale(date) : <EmptyValue />}</div>;
    },
  },
  columnUuid: commonColumns.columnUuid<RuleTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<RuleTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<RuleTableType>(),
};
